/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import swal from "sweetalert";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function LaporanHarian() {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const [openEditKegiatanModal, setOpenEditKegiatanModal] = useState(false);
  const [editingKegiatan, setEditingKegiatan] = useState(null);
  const [editKegiatanForm, setEditKegiatanForm] = useState({
    kegiatan: "",
    output: "",
    jumlah_output: "",
    satuan_output: "",
  });
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    type: "", // 'laporan_harian', 'instruksi', 'disposisi', 'events'
    title: "",
    data: [],
    tanggal: "",
  });
  const [laporanHarian, setLaporanHarian] = useState([]);
  const [dataDaftarHadir, setDataDaftarHadir] = useState([]);
  const [dateRange, setDateRange] = useState({
    tanggal_awal: "",
    tanggal_akhir: "",
  });
  const [filterDateRange, setFilterDateRange] = useState({
    tanggal_awal: "",
    tanggal_akhir: "",
  });

  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    // Gunakan uid_RSS sebagai prioritas, fallback ke uid
    const userIdentifier = user.uid_RSS || user.uid;
    setUserId(userIdentifier);
    setUserName(user.nama || user.name || "");
  }, []);

  const [form, setForm] = useState({
    uid_RSS: "",
    tanggal: "",
    kegiatan: [
      {
        kegiatan: "",
        output: "",
        jumlah_output: "",
        satuan_output: "",
      },
    ],
  });

  const satuanOutputOptions = [
    "dokumen",
    "item",
    "kegiatan",
    "record",
    "program",
    "sistem",
    "laporan",
    "file",
    "data",
    "proses",
    "kunjungan",
    "rapat",
    "koordinasi",
    "verifikasi",
    "input",
    "update",
    "maintenance",
    "monitoring",
    "evaluasi",
    "pelatihan",
    "workshop",
    "seminar",
    "survey",
    "audit",
    "inspeksi",
    "pemeriksaan",
    "analisis",
    "penelitian",
    "pengembangan",
    "implementasi",
  ];

  // Fungsi untuk mengambil data daftar hadir dari endpoint eksternal
  const fetchDaftarHadir = async (tanggal_awal, tanggal_akhir) => {
    if (!userId || !tanggal_awal || !tanggal_akhir) {
      return;
    }

    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/getdh/mingguan",
        {
          tAwal: tanggal_awal,
          tAkhir: tanggal_akhir,
          idUser: userId,
        }
      );

      if (response.data && response.data.success === 1) {
        setDataDaftarHadir(response.data.data || []);
      } else {
        setDataDaftarHadir([]);
      }
    } catch (err) {
      console.error("Error fetching daftar hadir:", err);
      setDataDaftarHadir([]);
    }
  };

  const fetchLaporanHarian = async (customDateRange = null) => {
    setLoading(true);

    let tanggal_awal, tanggal_akhir;

    if (customDateRange) {
      // Gunakan filter tanggal yang dipilih user
      tanggal_awal = customDateRange.tanggal_awal;
      tanggal_akhir = customDateRange.tanggal_akhir;
    } else {
      // Hitung tanggal awal (tanggal 1 bulan ini) dengan WITA (UTC+8)
      const today = new Date();
      const witaOffset = 8 * 60; // UTC+8 dalam menit
      const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

      tanggal_awal = new Date(witaDate.getFullYear(), witaDate.getMonth(), 1)
        .toISOString()
        .split("T")[0];

      // Hitung tanggal akhir (tanggal hari ini) dengan WITA
      tanggal_akhir = witaDate.toISOString().split("T")[0];
    }

    // Simpan rentang tanggal ke state
    setDateRange({
      tanggal_awal: tanggal_awal,
      tanggal_akhir: tanggal_akhir,
    });

    try {
      // Fetch kedua data secara bersamaan
      const [laporanResponse] = await Promise.all([
        axios.get(`${API_ESA_OFFICE}laporan-harian/pegawai/${userId}`, {
          params: {
            tanggal_awal: tanggal_awal,
            tanggal_akhir: tanggal_akhir,
          },
        }),
        fetchDaftarHadir(tanggal_awal, tanggal_akhir),
      ]);

      // Data baru sudah dalam format per hari dengan laporan_harian, instruksi, disposisi, events, apel_pagi, apel_siang
      setLaporanHarian(laporanResponse.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching laporan harian:", err);
      // Tetap fetch daftar hadir meskipun laporan error
      await fetchDaftarHadir(tanggal_awal, tanggal_akhir);
      setLoading(false);
      swal("Error", "Gagal mengambil data laporan harian", "error");
    }
  };

  useEffect(() => {
    if (userId) {
      // Inisialisasi filter tanggal dengan tanggal 1 bulan ini sampai hari ini dengan WITA (UTC+8)
      const today = new Date();
      const witaOffset = 8 * 60; // UTC+8 dalam menit
      const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

      const initialTanggalAwal = new Date(
        witaDate.getFullYear(),
        witaDate.getMonth(),
        1
      )
        .toISOString()
        .split("T")[0];
      const initialTanggalAkhir = witaDate.toISOString().split("T")[0];

      setFilterDateRange({
        tanggal_awal: initialTanggalAwal,
        tanggal_akhir: initialTanggalAkhir,
      });

      fetchLaporanHarian();
    }
  }, [userId]);

  const handleOpenDialog = () => {
    // Gunakan WITA untuk tanggal default form
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    setForm({
      uid_RSS: userId ? userId.toString() : "",
      tanggal: witaDate.toISOString().split("T")[0],
      kegiatan: [
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSubmitting(false);
    setForm({
      uid_RSS: "",
      tanggal: "",
      kegiatan: [
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
  };

  const handleOpenKegiatanModal = (id_laporan, tanggal) => {
    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan Laporan Harian - ${new Date(tanggal).toLocaleDateString(
        "id-ID"
      )}`
    );

    // Fetch kegiatan dari backend
    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${id_laporan}`)
      .then((res) => {
        setSelectedKegiatan(res.data.kegiatan || []);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        swal("Error", "Gagal mengambil data kegiatan", "error");
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const handleEditKegiatan = (kegiatan) => {
    setEditingKegiatan(kegiatan);
    setEditKegiatanForm({
      kegiatan: kegiatan.kegiatan,
      output: kegiatan.output,
      jumlah_output: kegiatan.jumlah_output.toString(),
      satuan_output: kegiatan.satuan_output,
    });
    setOpenEditKegiatanModal(true);
  };

  const handleCloseEditKegiatanModal = () => {
    setOpenEditKegiatanModal(false);
    setEditingKegiatan(null);
    setEditKegiatanForm({
      kegiatan: "",
      output: "",
      jumlah_output: "",
      satuan_output: "",
    });
  };

  const handleOpenDetailModal = (type, data, tanggal) => {
    let title = "";
    switch (type) {
      case "laporan_harian":
        title = `Detail Laporan Harian - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      case "instruksi":
        title = `Detail Instruksi Selesai - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "disposisi":
        title = `Detail Disposisi Selesai - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "events":
        title = `Detail Event - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      case "tamu":
        title = `Detail Tamu - ${new Date(tanggal).toLocaleDateString(
          "id-ID"
        )}`;
        break;
      default:
        title = "Detail";
    }
    setDetailModalData({
      type: type,
      title: title,
      data: data || [],
      tanggal: tanggal,
    });
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailModalData({
      type: "",
      title: "",
      data: [],
      tanggal: "",
    });
  };

  const handleUpdateKegiatan = () => {
    // Validasi form
    if (
      !editKegiatanForm.kegiatan ||
      !editKegiatanForm.output ||
      !editKegiatanForm.satuan_output
    ) {
      swal("Error", "Semua field harus diisi", "error");
      return;
    }

    if (
      !editKegiatanForm.jumlah_output ||
      editKegiatanForm.jumlah_output <= 0
    ) {
      swal("Error", "Jumlah output harus lebih dari 0", "error");
      return;
    }

    const updateData = {
      kegiatan: editKegiatanForm.kegiatan,
      output: editKegiatanForm.output,
      jumlah_output: parseInt(editKegiatanForm.jumlah_output),
      satuan_output: editKegiatanForm.satuan_output,
    };

    axios
      .put(
        `${API_ESA_OFFICE}laporan-harian/kegiatan/${editingKegiatan.id_kegiatan}`,
        updateData
      )
      .then((res) => {
        swal("Success", "Kegiatan berhasil diperbarui", "success");
        handleCloseEditKegiatanModal();
        // Refresh data kegiatan
        if (selectedKegiatan.length > 0) {
          const updatedKegiatan = selectedKegiatan.map((kegiatan) =>
            kegiatan.id_kegiatan === editingKegiatan.id_kegiatan
              ? { ...kegiatan, ...updateData }
              : kegiatan
          );
          setSelectedKegiatan(updatedKegiatan);
        }
      })
      .catch((err) => {
        console.error("Error updating kegiatan:", err);
        swal("Error", "Gagal memperbarui kegiatan", "error");
      });
  };

  const handleDeleteKegiatan = (kegiatan) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Kegiatan ini akan dihapus secara permanen!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axios
          .delete(
            `${API_ESA_OFFICE}laporan-harian/kegiatan/${kegiatan.id_kegiatan}`
          )
          .then((res) => {
            swal("Success", "Kegiatan berhasil dihapus", "success");
            // Remove kegiatan from list
            const updatedKegiatan = selectedKegiatan.filter(
              (item) => item.id_kegiatan !== kegiatan.id_kegiatan
            );
            setSelectedKegiatan(updatedKegiatan);
          })
          .catch((err) => {
            console.error("Error deleting kegiatan:", err);
            swal("Error", "Gagal menghapus kegiatan", "error");
          });
      }
    });
  };

  const handleAddKegiatan = () => {
    setForm({
      ...form,
      kegiatan: [
        ...form.kegiatan,
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
  };

  const handleRemoveKegiatan = (index) => {
    if (form.kegiatan.length > 1) {
      const newKegiatan = form.kegiatan.filter((_, i) => i !== index);
      setForm({
        ...form,
        kegiatan: newKegiatan,
      });
    }
  };

  const handleKegiatanChange = (index, field, value) => {
    const newKegiatan = [...form.kegiatan];
    newKegiatan[index][field] = value;
    setForm({
      ...form,
      kegiatan: newKegiatan,
    });
  };

  const handleSubmit = () => {
    // Prevent double execution
    if (submitting) {
      return;
    }

    // Validasi form
    if (!form.tanggal) {
      swal("Error", "Tanggal harus diisi", "error");
      return;
    }

    const hasEmptyKegiatan = form.kegiatan.some(
      (kegiatan) =>
        !kegiatan.kegiatan || !kegiatan.output || !kegiatan.satuan_output
    );

    if (hasEmptyKegiatan) {
      swal("Error", "Semua field kegiatan harus diisi", "error");
      return;
    }

    const hasInvalidJumlah = form.kegiatan.some(
      (kegiatan) => !kegiatan.jumlah_output || kegiatan.jumlah_output <= 0
    );

    if (hasInvalidJumlah) {
      swal("Error", "Jumlah output harus lebih dari 0", "error");
      return;
    }

    setSubmitting(true);

    const submitData = {
      uid_RSS: form.uid_RSS.toString(),
      tanggal: form.tanggal,
      kegiatan: form.kegiatan.map((kegiatan) => ({
        ...kegiatan,
        jumlah_output: parseInt(kegiatan.jumlah_output) || 0,
      })),
    };

    // Create new laporan
    axios
      .post(`${API_ESA_OFFICE}laporan-harian`, submitData)
      .then((res) => {
        swal("Success", "Laporan harian berhasil dibuat", "success");
        handleCloseDialog();
        fetchLaporanHarian();
      })
      .catch((err) => {
        console.error("Error creating laporan harian:", err);
        swal("Error", "Gagal membuat laporan harian", "error");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleDelete = (id) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Laporan harian akan dihapus secara permanen!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axios
          .delete(`${API_ESA_OFFICE}laporan-harian/${id}`)
          .then((res) => {
            swal("Success", "Laporan harian berhasil dihapus", "success");
            fetchLaporanHarian();
          })
          .catch((err) => {
            console.error("Error deleting laporan harian:", err);
            swal("Error", "Gagal menghapus laporan harian", "error");
          });
      }
    });
  };

  const handleDeleteKegiatanBatch = (laporanId, kegiatanIds) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Kegiatan yang dipilih akan dihapus!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const laporan = laporanHarian.find(
          (item) => item.id_laporan === laporanId
        );
        if (laporan) {
          axios
            .delete(
              `${API_ESA_OFFICE}laporan-harian/batch/${laporan.tanggal}/${laporan.uid_RSS}`,
              {
                data: { kegiatan_ids: kegiatanIds },
              }
            )
            .then((res) => {
              swal("Success", "Kegiatan berhasil dihapus", "success");
              fetchLaporanHarian();
            })
            .catch((err) => {
              console.error("Error deleting kegiatan:", err);
              swal("Error", "Gagal menghapus kegiatan", "error");
            });
        }
      }
    });
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = {
      ...filterDateRange,
      [field]: value,
    };

    setFilterDateRange(newDateRange);

    // Validasi tanggal
    if (newDateRange.tanggal_awal && newDateRange.tanggal_akhir) {
      if (newDateRange.tanggal_awal > newDateRange.tanggal_akhir) {
        swal(
          "Error",
          "Tanggal awal tidak boleh lebih besar dari tanggal akhir",
          "error"
        );
        return;
      }

      // Trigger fetch data dengan filter baru
      fetchLaporanHarian(newDateRange);
    }
  };

  const handleResetFilter = () => {
    // Reset filter tanggal ke default (tanggal 1 bulan ini sampai hari ini) dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const resetTanggalAwal = new Date(
      witaDate.getFullYear(),
      witaDate.getMonth(),
      1
    )
      .toISOString()
      .split("T")[0];
    const resetTanggalAkhir = witaDate.toISOString().split("T")[0];

    const resetDateRange = {
      tanggal_awal: resetTanggalAwal,
      tanggal_akhir: resetTanggalAkhir,
    };

    setFilterDateRange(resetDateRange);
    fetchLaporanHarian(resetDateRange);
  };

  const filteredLaporanHarian = useMemo(() => {
    return laporanHarian;
  }, [laporanHarian]);

  // Fungsi untuk menormalisasi tanggal ke format YYYY-MM-DD
  const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return null;
  };

  const columns = [
    { Header: "No", accessor: "index", width: "6%" },
    {
      Header: "Tanggal",
      accessor: "tanggal",
      width: "100px",
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Waktu Masuk & Apel Pagi",
      accessor: "masuk_apel_pagi",
      width: "13%",
    },
    {
      Header: "Laporan Harian & Aksi",
      accessor: "laporan_harian",
      width: "14%",
    },
    {
      Header: "Instruksi Selesai",
      accessor: "instruksi",
      width: "140px",
      minWidth: 140,
      maxWidth: 160,
    },
    {
      Header: "Disposisi Selesai",
      accessor: "disposisi",
      width: "140px",
      minWidth: 140,
      maxWidth: 160,
    },
    {
      Header: "Event",
      accessor: "events",
      width: "140px",
      minWidth: 140,
      maxWidth: 160,
    },
    {
      Header: "Tamu",
      accessor: "tamu",
      width: "140px",
      minWidth: 140,
      maxWidth: 160,
    },
    {
      Header: "Waktu Pulang & Apel Siang",
      accessor: "pulang_apel_siang",
      width: "14%",
    },
  ];

  const rows = filteredLaporanHarian.map((item, index) => {
    // Cari laporan harian pertama untuk aksi (jika ada)
    const firstLaporan =
      item.laporan_harian && item.laporan_harian.length > 0
        ? item.laporan_harian[0]
        : null;

    // Cari data daftar hadir untuk tanggal ini
    const tanggalNormalized = normalizeDate(item.tanggal);
    const daftarHadirData = dataDaftarHadir.find(
      (dh) => normalizeDate(dh.tanggal) === tanggalNormalized
    );

    return {
      index: index + 1,
      tanggal: (
        <MDBox
          sx={{
            maxWidth: 100,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          <MDTypography variant="caption" color="text">
            {new Date(item.tanggal).toLocaleDateString("id-ID", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </MDTypography>
        </MDBox>
      ),
      masuk_apel_pagi: (
        <MDBox>
          {daftarHadirData?.waktuMasuk && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">
                {daftarHadirData.status === "Hadir" ||
                daftarHadirData.status === "Dinas Luar"
                  ? "Masuk"
                  : "Lapor"}
                : {daftarHadirData.waktuMasuk}
              </MDTypography>
              {daftarHadirData.status && (
                <Chip
                  label={daftarHadirData.status}
                  color={
                    daftarHadirData.status === "Hadir"
                      ? "success"
                      : daftarHadirData.status === "Izin"
                      ? "warning"
                      : "error"
                  }
                  size="small"
                  variant="filled"
                  sx={{ mt: 0.5 }}
                />
              )}
            </MDBox>
          )}
          {item.apel_pagi ? (
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              mt={0.5}
            >
              Apel: {item.apel_pagi.jam_absen || "-"}
            </MDTypography>
          ) : (
            <MDTypography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Belum Apel Pagi
            </MDTypography>
          )}
        </MDBox>
      ),
      laporan_harian: (
        <MDBox>
          {item.laporan_harian && item.laporan_harian.length > 0 ? (
            <MDBox>
              <MDBox mb={1}>
                <Chip
                  label={`${item.laporan_harian.length} Laporan`}
                  color="info"
                  size="small"
                  sx={{ mb: 0.5, cursor: "pointer" }}
                  onClick={() =>
                    handleOpenDetailModal(
                      "laporan_harian",
                      item.laporan_harian,
                      item.tanggal
                    )
                  }
                />
                <MDTypography variant="caption" color="text" display="block">
                  {item.laporan_harian.reduce(
                    (sum, l) => sum + (l.jumlah_kegiatan || 0),
                    0
                  )}{" "}
                  Kegiatan
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() =>
                    handleOpenKegiatanModal(
                      firstLaporan.id_laporan,
                      item.tanggal
                    )
                  }
                >
                  Lihat Kegiatan
                </MDButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleDelete(firstLaporan.id_laporan)}
                >
                  <DeleteIcon />
                </IconButton>
              </MDBox>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      instruksi: (
        <MDBox
          sx={{
            maxWidth: 140,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {(() => {
            const instruksiTerima =
              item.instruksi?.filter((i) => i.jenis === "terima") || [];
            const instruksiBeri =
              item.instruksi?.filter((i) => i.jenis === "beri") || [];
            const totalInstruksi = item.instruksi?.length || 0;

            if (totalInstruksi === 0) {
              return (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              );
            }

            return (
              <MDBox>
                {instruksiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip
                      label={`Terima: ${instruksiTerima.length}`}
                      color="success"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "instruksi",
                          instruksiTerima,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      {instruksiTerima[0].perihal || "-"}
                    </MDTypography>
                  </MDBox>
                )}
                {instruksiBeri.length > 0 && (
                  <MDBox>
                    <Chip
                      label={`Beri: ${instruksiBeri.length}`}
                      color="info"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "instruksi",
                          instruksiBeri,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      {instruksiBeri[0].perihal || "-"}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      disposisi: (
        <MDBox
          sx={{
            maxWidth: 140,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {(() => {
            const disposisiTerima =
              item.disposisi?.filter((d) => d.jenis === "terima") || [];
            const disposisiBeri =
              item.disposisi?.filter((d) => d.jenis === "beri") || [];
            const totalDisposisi = item.disposisi?.length || 0;

            if (totalDisposisi === 0) {
              return (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              );
            }

            return (
              <MDBox>
                {disposisiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip
                      label={`Terima: ${disposisiTerima.length}`}
                      color="warning"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "disposisi",
                          disposisiTerima,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      {disposisiTerima[0].perihal ||
                        disposisiTerima[0].nomor_surat ||
                        "-"}
                    </MDTypography>
                  </MDBox>
                )}
                {disposisiBeri.length > 0 && (
                  <MDBox>
                    <Chip
                      label={`Beri: ${disposisiBeri.length}`}
                      color="info"
                      size="small"
                      sx={{ mb: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        handleOpenDetailModal(
                          "disposisi",
                          disposisiBeri,
                          item.tanggal
                        )
                      }
                    />
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      {disposisiBeri[0].perihal ||
                        disposisiBeri[0].nomor_surat ||
                        "-"}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      events: (
        <MDBox
          sx={{
            maxWidth: 140,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {item.events && item.events.length > 0 ? (
            <MDBox>
              <Chip
                label={`${item.events.length} Event`}
                color="primary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("events", item.events, item.tanggal)
                }
              />
              <MDTypography variant="caption" color="text" display="block">
                {item.events[0].nama_event || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      tamu: (
        <MDBox
          sx={{
            maxWidth: 140,
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {item.tamu && item.tamu.length > 0 ? (
            <MDBox>
              <Chip
                label={`${item.tamu.length} Tamu`}
                color="secondary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("tamu", item.tamu, item.tanggal)
                }
              />
              <MDTypography variant="caption" color="text" display="block">
                {item.tamu[0].nama_tamu || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          )}
        </MDBox>
      ),
      pulang_apel_siang: (
        <MDBox>
          {daftarHadirData?.waktuPulang && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">
                Pulang: {daftarHadirData.waktuPulang}
              </MDTypography>
              {daftarHadirData.workingHours && (
                <MDTypography
                  variant="caption"
                  color="info"
                  display="block"
                  sx={{ fontWeight: "bold", mt: 0.5 }}
                >
                  Jam Kerja: {daftarHadirData.workingHours}
                </MDTypography>
              )}
            </MDBox>
          )}
          {item.apel_siang ? (
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              mt={0.5}
            >
              Apel: {item.apel_siang.jam_absen || "-"}
            </MDTypography>
          ) : (
            <MDTypography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Belum Apel Siang
            </MDTypography>
          )}
        </MDBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
              >
                <MDBox>
                  <MDTypography variant="h6" gutterBottom>
                    Laporan Harian
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Kelola laporan harian Anda
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="info"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Menampilkan data dari{" "}
                    {filterDateRange.tanggal_awal
                      ? new Date(
                          filterDateRange.tanggal_awal
                        ).toLocaleDateString("id-ID")
                      : "..."}{" "}
                    hingga{" "}
                    {filterDateRange.tanggal_akhir
                      ? new Date(
                          filterDateRange.tanggal_akhir
                        ).toLocaleDateString("id-ID")
                      : "..."}
                    {filterDateRange.tanggal_awal &&
                      filterDateRange.tanggal_akhir && (
                        <span style={{ color: "#2196f3", fontWeight: "bold" }}>
                          {" "}
                          (Filter Aktif)
                        </span>
                      )}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleOpenDialog}
                  startIcon={<AddIcon />}
                >
                  Tambah Laporan
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center" mb={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Tanggal Awal"
                      value={filterDateRange.tanggal_awal}
                      onChange={(e) =>
                        handleDateRangeChange("tanggal_awal", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Tanggal Akhir"
                      value={filterDateRange.tanggal_akhir}
                      onChange={(e) =>
                        handleDateRangeChange("tanggal_akhir", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={handleResetFilter}
                      fullWidth
                    >
                      Reset Filter
                    </MDButton>
                  </Grid>
                </Grid>

                {isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {loading && (
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Memuat data...
                        </MDTypography>
                      </MDBox>
                    )}
                    {!loading && filteredLaporanHarian.length === 0 && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <Icon
                          sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                        >
                          description
                        </Icon>
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada laporan harian
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          Buat laporan harian pertama Anda
                        </MDTypography>
                      </MDBox>
                    )}
                    {!loading &&
                      filteredLaporanHarian.length > 0 &&
                      filteredLaporanHarian.map((item, index) => {
                        const firstLaporan =
                          item.laporan_harian && item.laporan_harian.length > 0
                            ? item.laporan_harian[0]
                            : null;
                        const totalKegiatan = item.laporan_harian
                          ? item.laporan_harian.reduce(
                              (sum, l) => sum + (l.jumlah_kegiatan || 0),
                              0
                            )
                          : 0;

                        // Cari data daftar hadir untuk tanggal ini
                        const tanggalNormalized = normalizeDate(item.tanggal);
                        const daftarHadirData = dataDaftarHadir.find(
                          (dh) =>
                            normalizeDate(dh.tanggal) === tanggalNormalized
                        );

                        return (
                          <Card key={`laporan-${item.tanggal || index}`}>
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <MDTypography
                                      variant="button"
                                      fontWeight="bold"
                                    >
                                      #{index + 1}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {new Date(
                                        item.tanggal
                                      ).toLocaleDateString("id-ID", {
                                        weekday: "long",
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      })}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>

                                {/* Waktu Masuk & Apel Pagi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e8f5e9",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="success"
                                    >
                                      Masuk & Apel Pagi
                                    </MDTypography>
                                    {daftarHadirData?.waktuMasuk && (
                                      <MDBox mt={0.5}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          {daftarHadirData.status === "Hadir" ||
                                          daftarHadirData.status ===
                                            "Dinas Luar"
                                            ? "Masuk"
                                            : "Lapor"}
                                          : {daftarHadirData.waktuMasuk}
                                        </MDTypography>
                                        {daftarHadirData.status && (
                                          <Chip
                                            label={daftarHadirData.status}
                                            color={
                                              daftarHadirData.status === "Hadir"
                                                ? "success"
                                                : daftarHadirData.status ===
                                                  "Izin"
                                                ? "warning"
                                                : "error"
                                            }
                                            size="small"
                                            variant="filled"
                                            sx={{ mt: 0.5 }}
                                          />
                                        )}
                                      </MDBox>
                                    )}
                                    {item.apel_pagi ? (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        Jam Apel:{" "}
                                        {item.apel_pagi.jam_absen || "-"}
                                      </MDTypography>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Belum Apel Pagi
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Laporan Harian */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e3f2fd",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="info"
                                    >
                                      Laporan Harian
                                    </MDTypography>
                                    {item.laporan_harian &&
                                    item.laporan_harian.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.laporan_harian.length} Laporan - ${totalKegiatan} Kegiatan`}
                                          color="info"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "laporan_harian",
                                              item.laporan_harian,
                                              item.tanggal
                                            )
                                          }
                                        />
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada laporan
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Instruksi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#e8f5e9",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="success"
                                    >
                                      Instruksi
                                    </MDTypography>
                                    {(() => {
                                      const instruksiTerima =
                                        item.instruksi?.filter(
                                          (i) => i.jenis === "terima"
                                        ) || [];
                                      const instruksiBeri =
                                        item.instruksi?.filter(
                                          (i) => i.jenis === "beri"
                                        ) || [];
                                      const totalInstruksi =
                                        item.instruksi?.length || 0;

                                      if (totalInstruksi === 0) {
                                        return (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                          >
                                            Tidak ada instruksi
                                          </MDTypography>
                                        );
                                      }

                                      return (
                                        <MDBox mt={0.5}>
                                          {instruksiTerima.length > 0 && (
                                            <MDBox mb={1}>
                                              <Chip
                                                label={`Terima: ${instruksiTerima.length}`}
                                                color="success"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "instruksi",
                                                    instruksiTerima,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {instruksiTerima
                                                .slice(0, 2)
                                                .map((inst, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    • {inst.perihal || "-"}
                                                  </MDTypography>
                                                ))}
                                              {instruksiTerima.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{instruksiTerima.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                          {instruksiBeri.length > 0 && (
                                            <MDBox>
                                              <Chip
                                                label={`Beri: ${instruksiBeri.length}`}
                                                color="info"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "instruksi",
                                                    instruksiBeri,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {instruksiBeri
                                                .slice(0, 2)
                                                .map((inst, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    • {inst.perihal || "-"}
                                                  </MDTypography>
                                                ))}
                                              {instruksiBeri.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{instruksiBeri.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                        </MDBox>
                                      );
                                    })()}
                                  </MDBox>
                                </Grid>

                                {/* Disposisi */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fff3e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="warning"
                                    >
                                      Disposisi
                                    </MDTypography>
                                    {(() => {
                                      const disposisiTerima =
                                        item.disposisi?.filter(
                                          (d) => d.jenis === "terima"
                                        ) || [];
                                      const disposisiBeri =
                                        item.disposisi?.filter(
                                          (d) => d.jenis === "beri"
                                        ) || [];
                                      const totalDisposisi =
                                        item.disposisi?.length || 0;

                                      if (totalDisposisi === 0) {
                                        return (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                          >
                                            Tidak ada disposisi
                                          </MDTypography>
                                        );
                                      }

                                      return (
                                        <MDBox mt={0.5}>
                                          {disposisiTerima.length > 0 && (
                                            <MDBox mb={1}>
                                              <Chip
                                                label={`Terima: ${disposisiTerima.length}`}
                                                color="warning"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "disposisi",
                                                    disposisiTerima,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {disposisiTerima
                                                .slice(0, 2)
                                                .map((disp, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    •{" "}
                                                    {disp.perihal ||
                                                      disp.nomor_surat ||
                                                      "-"}
                                                  </MDTypography>
                                                ))}
                                              {disposisiTerima.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{disposisiTerima.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                          {disposisiBeri.length > 0 && (
                                            <MDBox>
                                              <Chip
                                                label={`Beri: ${disposisiBeri.length}`}
                                                color="info"
                                                size="small"
                                                sx={{
                                                  mb: 0.5,
                                                  cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                  handleOpenDetailModal(
                                                    "disposisi",
                                                    disposisiBeri,
                                                    item.tanggal
                                                  )
                                                }
                                              />
                                              {disposisiBeri
                                                .slice(0, 2)
                                                .map((disp, idx) => (
                                                  <MDTypography
                                                    key={idx}
                                                    variant="caption"
                                                    color="text"
                                                    display="block"
                                                  >
                                                    •{" "}
                                                    {disp.perihal ||
                                                      disp.nomor_surat ||
                                                      "-"}
                                                  </MDTypography>
                                                ))}
                                              {disposisiBeri.length > 2 && (
                                                <MDTypography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  +{disposisiBeri.length - 2}{" "}
                                                  lainnya
                                                </MDTypography>
                                              )}
                                            </MDBox>
                                          )}
                                        </MDBox>
                                      );
                                    })()}
                                  </MDBox>
                                </Grid>

                                {/* Events */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#f3e5f5",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="primary"
                                    >
                                      Event
                                    </MDTypography>
                                    {item.events && item.events.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.events.length} Event`}
                                          color="primary"
                                          size="small"
                                          sx={{ mb: 0.5, cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "events",
                                              item.events,
                                              item.tanggal
                                            )
                                          }
                                        />
                                        {item.events
                                          .slice(0, 2)
                                          .map((evt, idx) => (
                                            <MDTypography
                                              key={idx}
                                              variant="caption"
                                              color="text"
                                              display="block"
                                            >
                                              • {evt.nama_event || "-"}
                                            </MDTypography>
                                          ))}
                                        {item.events.length > 2 && (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            +{item.events.length - 2} lainnya
                                          </MDTypography>
                                        )}
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada event
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Tamu */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fce4ec",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="secondary"
                                    >
                                      Tamu
                                    </MDTypography>
                                    {item.tamu && item.tamu.length > 0 ? (
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${item.tamu.length} Tamu`}
                                          color="secondary"
                                          size="small"
                                          sx={{ mb: 0.5, cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "tamu",
                                              item.tamu,
                                              item.tanggal
                                            )
                                          }
                                        />
                                        {item.tamu
                                          .slice(0, 2)
                                          .map((tamu, idx) => (
                                            <MDTypography
                                              key={idx}
                                              variant="caption"
                                              color="text"
                                              display="block"
                                            >
                                              • {tamu.nama_tamu || "-"}
                                            </MDTypography>
                                          ))}
                                        {item.tamu.length > 2 && (
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            +{item.tamu.length - 2} lainnya
                                          </MDTypography>
                                        )}
                                      </MDBox>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Tidak ada tamu
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Waktu Pulang & Apel Siang */}
                                <Grid item xs={12}>
                                  <MDBox
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "#fff3e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="warning"
                                    >
                                      Pulang & Apel Siang
                                    </MDTypography>
                                    {daftarHadirData?.waktuPulang && (
                                      <MDBox mt={0.5}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Waktu Pulang:{" "}
                                          {daftarHadirData.waktuPulang}
                                        </MDTypography>
                                        {daftarHadirData.workingHours && (
                                          <Chip
                                            label={`Jam Kerja: ${daftarHadirData.workingHours}`}
                                            color="info"
                                            size="small"
                                            variant="filled"
                                            sx={{ mt: 0.5 }}
                                          />
                                        )}
                                      </MDBox>
                                    )}
                                    {item.apel_siang ? (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        Jam Apel:{" "}
                                        {item.apel_siang.jam_absen || "-"}
                                      </MDTypography>
                                    ) : (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Belum Apel Siang
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                </Grid>

                                {/* Aksi */}
                                {firstLaporan && (
                                  <Grid item xs={12}>
                                    <MDBox mt={1} display="flex" gap={1}>
                                      <MDButton
                                        variant="gradient"
                                        color="info"
                                        size="small"
                                        onClick={() =>
                                          handleOpenKegiatanModal(
                                            firstLaporan.id_laporan,
                                            item.tanggal
                                          )
                                        }
                                        sx={{ minWidth: "auto", px: 1 }}
                                      >
                                        <Icon sx={{ fontSize: 16 }}>
                                          visibility
                                        </Icon>
                                        &nbsp;Lihat Kegiatan
                                      </MDButton>
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() =>
                                          handleDelete(firstLaporan.id_laporan)
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </MDBox>
                                  </Grid>
                                )}
                              </Grid>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </MDBox>
                ) : (
                  <MDBox sx={{ "& table": { tableLayout: "fixed" } }}>
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Dialog Form */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Tambah Laporan Harian</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tanggal"
                    type="date"
                    value={form.tanggal}
                    onChange={(e) =>
                      setForm({ ...form, tanggal: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nama Pegawai"
                    value={userName}
                    variant="outlined"
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDTypography variant="h6">Daftar Kegiatan</MDTypography>
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={handleAddKegiatan}
                      startIcon={<AddIcon />}
                    >
                      Tambah Kegiatan
                    </MDButton>
                  </MDBox>

                  {form.kegiatan.map((kegiatan, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <MDTypography variant="h6">
                          Kegiatan {index + 1}
                        </MDTypography>
                        {form.kegiatan.length > 1 && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveKegiatan(index)}
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </MDBox>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Kegiatan"
                            value={kegiatan.kegiatan}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "kegiatan",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Output"
                            value={kegiatan.output}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "output",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Jumlah Output"
                            type="number"
                            value={kegiatan.jumlah_output}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "jumlah_output",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            inputProps={{ min: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={satuanOutputOptions}
                            value={kegiatan.satuan_output}
                            onChange={(event, newValue) =>
                              handleKegiatanChange(
                                index,
                                "satuan_output",
                                newValue || ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Satuan Output"
                                variant="outlined"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {submitting ? "Menyimpan..." : "Simpan"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Kegiatan */}
        <Dialog
          open={openKegiatanModal}
          onClose={handleCloseKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {loadingKegiatan ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Memuat data kegiatan...
                  </MDTypography>
                </MDBox>
              ) : selectedKegiatan.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada kegiatan untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Total Kegiatan: {selectedKegiatan.length}
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    {selectedKegiatan.map((kegiatan, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, mb: 2 }}>
                          <MDBox>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <MDTypography
                                variant="h6"
                                color="info"
                                gutterBottom
                              >
                                Kegiatan {index + 1}
                              </MDTypography>
                              <MDBox display="flex" gap={1}>
                                <IconButton
                                  color="info"
                                  size="small"
                                  onClick={() => handleEditKegiatan(kegiatan)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteKegiatan(kegiatan)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </MDBox>
                            </MDBox>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Deskripsi Kegiatan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.kegiatan}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Jumlah Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.jumlah_output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Satuan Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.satuan_output}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Edit Kegiatan */}
        <Dialog
          open={openEditKegiatanModal}
          onClose={handleCloseEditKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Edit Kegiatan</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Kegiatan"
                    value={editKegiatanForm.kegiatan}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        kegiatan: e.target.value,
                      })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Output"
                    value={editKegiatanForm.output}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        output: e.target.value,
                      })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Jumlah Output"
                    type="number"
                    value={editKegiatanForm.jumlah_output}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        jumlah_output: e.target.value,
                      })
                    }
                    variant="outlined"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={satuanOutputOptions}
                    value={editKegiatanForm.satuan_output}
                    onChange={(event, newValue) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        satuan_output: newValue || "",
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Satuan Output"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseEditKegiatanModal}>
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleUpdateKegiatan}
            >
              Update
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Detail Modal */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{detailModalData.title}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {detailModalData.data.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada data untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  {detailModalData.type === "laporan_harian" && (
                    <MDBox>
                      {detailModalData.data.map((laporan, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="info"
                              gutterBottom
                            >
                              Laporan #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tanggal:{" "}
                              {new Date(laporan.tanggal).toLocaleDateString(
                                "id-ID"
                              )}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Jumlah Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.jumlah_kegiatan || 0}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                ID Laporan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.id_laporan}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "instruksi" && (
                    <MDBox>
                      {detailModalData.data.map((instruksi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="success"
                              gutterBottom
                            >
                              Instruksi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {instruksi.waktu_selesai
                                ? new Date(
                                    instruksi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Isi Instruksi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.isi_instruksi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Prioritas:
                              </MDTypography>
                              <Chip
                                label={instruksi.prioritas || "-"}
                                color={
                                  instruksi.prioritas === "Tinggi"
                                    ? "error"
                                    : instruksi.prioritas === "Sedang"
                                    ? "warning"
                                    : "default"
                                }
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "disposisi" && (
                    <MDBox>
                      {detailModalData.data.map((disposisi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="warning"
                              gutterBottom
                            >
                              Disposisi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {disposisi.waktu_selesai
                                ? new Date(
                                    disposisi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nomor Surat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.nomor_surat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "events" && (
                    <MDBox>
                      {detailModalData.data.map((event, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="primary"
                              gutterBottom
                            >
                              Event #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Absen:{" "}
                              {event.waktu
                                ? new Date(event.waktu).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Event:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.nama_event || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Detail Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.detail_kegiatan || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Tempat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.tempat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Mulai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_mulai
                                  ? new Date(event.waktu_mulai).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Selesai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_finish
                                  ? new Date(event.waktu_finish).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "tamu" && (
                    <MDBox>
                      {detailModalData.data.map((tamu, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="secondary"
                              gutterBottom
                            >
                              Tamu #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Kedatangan:{" "}
                              {tamu.waktu_kedatangan
                                ? new Date(
                                    tamu.waktu_kedatangan
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Tamu:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_tamu || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Instansi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_instansi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Keperluan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.keperluan || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default LaporanHarian;
